<template>
  <v-layout column>
    <v-layout :column="$vuetify.breakpoint.xs">
      <v-flex>
        <v-layout column>
          <v-flex v-for="field in attendeeFields" :key="field.key" class="pb-16">
            <component
              :is="field.component"
              v-model="form[field.key]"
              :validator="vs && vs.form[field.key]"
              v-bind="field"
              :error-messages="{
                ...field.errorMessages,
              }"
            />
            <slot v-if="field.key && field.key.toLowerCase() === 'email'" name="after-email" />
          </v-flex>
          <v-checkbox
            v-model="form.linkLocationWithUserProfile"
            style="margin-top: 0"
            :label="$t('linkLocationWithUserProfileCheckbox')"
            color="#FF3300"
          />
          <!--events with old attendee fields - remove when events are over-->
          <v-flex v-if="containsOldAttendeeFields" class="pb-16">
            <text-field
              id="address"
              v-model.trim="form.address"
              :label="$t('address')"
              type="text"
            />
          </v-flex>
          <!--custom fields for event.id = 2445 - delete div when event is over-->
          <div v-if="Number(event.id) === 2445">
            <v-flex class="pb-16">
              <text-field
                id="companyOrganization"
                v-model.trim="form.companyOrganization"
                :label="$t('companyOrganization')"
                type="text"
              />
            </v-flex>
            <v-flex class="pb-16">
              <text-field
                id="function"
                v-model.trim="form.function"
                :label="$t('function')"
                type="text"
              />
            </v-flex>
          </div>
          <div v-if="isGiftCardSelected" class="gift-card-input-container">
            <div class="gift-card-input-header">
              <div class="gift-card-container-title">{{ $t('giftCardDataTitle') }}</div>
              <div class="gift-card-description">{{ $t('giftCardDescription') }}</div>
            </div>
            <div class="gift-card-inputs">
              <text-field
                id="gift-card-code"
                v-model.trim="form.giftCardCode"
                :label="$t('giftCardCode')"
                type="text"
                box
                :validator="vs.form.giftCardCode"
                :error-messages="{
                  minLength: 'Gift card code must have at least 10 digits.',
                  maxLength: 'Gift card code can have no more than 20 digits.',
                  isSuccessfullyApplied: 'Active gift card with this promo code does not exist.',
                }"
                style="flex: 3; min-width: 14.025rem"
              >
                <template v-if="isSuccessfullyAppliedGiftCard" v-slot:append>
                  <div class="successful-gift-card-confirmation">
                    <span class="availability-text">
                      ({{ $t('giftCardAvailability') }} {{ totalGiftCardAmount }} EUR)
                    </span>
                    <img
                      src="/assets/success-icon.svg"
                      alt="Successful icon"
                      style="height: 1.5rem; width: 1.5rem"
                    >
                  </div>
                </template>
              </text-field>
              <text-field
                id="gift-card-amount"
                v-model.trim="form.giftCardAmount"
                :label="$t('giftCardAmount')"
                type="text"
                box
                :validator="vs.form.giftCardAmount"
                :error-messages="{
                  decimal: 'Enter valid price.',
                }"
                style="flex: 2; min-width: 9.4rem"
              />
            </div>
          </div>

          <div class="receipt-container">
            <div class="subtitle-checkout">
              {{ $t('receiptInfo') }}
            </div>
            <receipt-form
              :vs="vs"
              :disable-r1-invoice="isGiftCardSelected"
              :event-id="event && event.id"
              @values="form = { ...form, ...$event }"
            />
          </div>
          <slot name="before-terms" />
          <div :class="['terms-checkbox', !isZkm && 'terms-checkbox-regular']">
            <checkbox
              id="core-event-terms"
              v-model="form.acceptTermsAndConditions"
              :error-messages="{
                sameAs:
                  'Za nastavak kupnje potrebno je prihvatiti CoreEvent uvjete korištenja i uvjete prodaje.',
              }"
              :validator="vs.form.acceptTermsAndConditions"
            >
              <div>
                {{ $t('acceptCoreEvent') }}
                <a id="terms" class="terms" :href="`${landingURL}/terms`" target="_blank">
                  {{ $t('contractForCustomers') }} </a>.*
              </div>
            </checkbox>
          </div>
          <checkbox v-model="form.getSubscription" style="align-self: flex-start">
            <label class="about-subscription">
              <template v-if="isEuropeanGymnasticsOrganization">
                {{ $t('marketingConsentEuropeanGymnastics') }}
              </template>
              <template v-else>
                {{ $t('marketingConsent') }}
                <a id="sale" class="terms" :href="`${landingURL}/personal-data`" target="_blank">
                  {{ $t('gdprText') }}
                </a>
              </template>
            </label>
          </checkbox>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-layout>
</template>

<script>
import ReceiptForm from './receipt-form.vue'
import { landingURL, normalizePostalCodeValue } from '../../utils'
import { GET_GIFT_CARD_BY_CODE } from '@/graphql/giftCard.graphql'

const countries = require('../../assets/countries')

export default {
  name: 'checkout-form',
  components: {
    ReceiptForm,
  },
  props: {
    event: {
      type: Object,
      default: null,
    },
    isZkm: {
      default: false,
      type: Boolean,
    },
    category: {
      type: Object,
      default: () => {},
    },
    initialValues: {
      type: Object,
      default: () => ({}),
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default: () => [],
    },
    vs: {
      type: Object,
      default: () => ({}),
    },
    user: {
      type: Object,
      default: () => {},
    },
    isGiftCardSelected: {
      type: Boolean,
      default: false,
    },
    totalOrderPrice: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      form: {
        linkLocationWithUserProfile: false,
        giftCardCode: undefined,
        giftCardAmount: undefined,
        acceptTermsAndConditions: false,
        getSubscription: false,
      },
      password: null,
      countries,
      landingURL,
      isSuccessfullyAppliedGiftCard: false,
      maxGiftCardAmount: undefined,
      totalGiftCardAmount: undefined,
      attendeeFields: this.fields.filter(field => !['Document Id', 'Function'].includes(field.key)),
    }
  },
  computed: {
    containsOldAttendeeFields() {
      return this.fields.some(field => ['Document Id', 'Function'].includes(field.key))
    },
    isEuropeanGymnasticsOrganization() {
      return Number(this.event.organization.id) === 625
    },
  },
  watch: {
    '$i18next.language': function () {
      this.setAttendeeFields(this.fields)
    },
    fields: {
      handler(fields) {
        this.setAttendeeFields(fields)
      },
      deep: true,
    },
    user: {
      handler: function (userData) {
        this.populateFormWithUserData(userData)
      },
      immediate: true,
    },
    form: {
      handler(val) {
        this.$emit('values', val)
      },
      deep: true,
    },
    'form.giftCardCode': async function () {
      if (this.form.giftCardCode.length >= 10 && this.form.giftCardCode.length <= 20) {
        const {
          data: { getGiftCardByCode: result },
        } = await this.$apollo.query({
          query: GET_GIFT_CARD_BY_CODE,
          variables: { code: this.form.giftCardCode, organizationId: this.event.organization.id },
        })
        this.isSuccessfullyAppliedGiftCard = !!result && result.active
        if (this.isSuccessfullyAppliedGiftCard) {
          this.totalGiftCardAmount = result.amount
          this.maxGiftCardAmount = Math.min(
            result.amount,
            Number(this.totalOrderPrice.split(' ')[1]),
          )
          this.$emit('applyGiftCard', {
            code: this.form.giftCardCode,
            amount: this.form.giftCardAmount,
            isInRange:
              Number(this.form.giftCardAmount) > 0 &&
              Number(this.form.giftCardAmount) <= this.maxGiftCardAmount,
          })
        }
      } else {
        this.isSuccessfullyAppliedGiftCard = false
      }
    },
    'form.giftCardAmount': function (val) {
      if (this.isSuccessfullyAppliedGiftCard) {
        this.$emit('applyGiftCard', {
          code: this.form.giftCardCode,
          amount: this.form.giftCardAmount,
          isInRange: Number(val) > 0 && Number(val) <= this.maxGiftCardAmount,
        })
      }
    },
    isSuccessfullyAppliedGiftCard: function (val, oldVal) {
      if (oldVal && !val) {
        this.$emit('applyGiftCard', null)
      }
    },
  },
  mounted() {
    const initial = { zones: [], ...this.initialValues }
    const formFromQuery = Object.entries(this.$route.query).reduce((acc, [key, value]) => {
      const replaced = key.split('_').join(' ')
      if (!value || !this.fields.find(f => f.key === replaced)) return acc
      acc[replaced] = value

      return acc
    }, initial)
    this.form = normalizePostalCodeValue({ ...this.form, ...formFromQuery })
  },
  methods: {
    setAttendeeFields(fields) {
      this.attendeeFields = (fields || []).filter(
        field => !['Document Id', 'Function'].includes(field.key),
      )
    },
    populateFormWithUserData(userData) {
      if (userData) {
        const locationData = userData.location
          ? {
            Address: userData.location.Address,
            City: userData.location.City,
            'Postal code': userData.location['Postal code'],
            Country: userData.location.Country,
          }
          : {}
        this.form = normalizePostalCodeValue({
          ...this.form,
          'First name': userData.firstName,
          'Last name': userData.lastName,
          Email: userData.email,
          ...locationData,
        })
      }
    },
  },
}
</script>

<style scoped>
.terms {
  color: #333333 !important;
  text-decoration: underline;
}

.terms-checkbox {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.v-input_slot {
  align-items: flex-start !important;
}

.terms-checkbox-regular {
  border-top: 1px solid rgba(151, 151, 151, 0.5);
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-top: 2rem;
}

.receipt-container {
  border-top: 1px solid rgba(151, 151, 151, 0.5);
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.subtitle-checkout {
  font-family: Raleway, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 28px;
  color: #333333;
  margin-bottom: 20px;
  margin-top: 30px;
}

.gift-card-input-container {
  display: flex;
  padding: 2rem 1.5rem 2.5rem 1.5rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 2.5rem;
  align-self: stretch;

  border-top: 1px solid rgba(151, 151, 151, 0.5);
  border-bottom: 1px solid rgba(151, 151, 151, 0.5);
  margin: 0 -1.5rem 2rem -1.5rem;
}

.gift-card-container-title {
  font-family: Raleway, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 28px;
  color: #333333;
}

.gift-card-input-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
}

.gift-card-inputs {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  align-self: stretch;
  overflow: hidden;
  flex-wrap: wrap;
}

.gift-card-description {
  color: #333;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.availability-text {
  color: #979797;
  font-family: Roboto;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: 0.03125rem;
}

.successful-gift-card-confirmation {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-left: 5px;
}
</style>
